import { baseApi } from '@services/api/baseApi';
import {
  invalidateTagsOnSuccessConst,
  providesTagsOnSuccessConst,
} from '@services/api/rtkqTagHelpers';

import { authFailed, authSucceeded, loggedOut } from './authSlice';
import {
  type BackendAuthResponse,
  profileToUser,
  userFromAuthResponse,
  type UserProfileDTO,
} from './authTransforms';
import type { AuthTokens, User } from './types';
import { tokenStorage } from './utils/tokenStorage';

export type { BackendAuthResponse } from './authTransforms';
export { profileToUser, userFromAuthResponse } from './authTransforms';

interface RefreshResponse {
  tokens: AuthTokens;
  user: User;
}

interface LoginRequest {
  email: string;
  password: string;
  /** Client-only flag: when false, tokens are kept in `sessionStorage`. */
  remember?: boolean;
}

interface LoginResponse {
  tokens: AuthTokens;
  user: User;
}

function normalizeAuthResponse(data: BackendAuthResponse): LoginResponse {
  const tokens: AuthTokens = {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };

  return {
    tokens,
    user: userFromAuthResponse(data),
  };
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
  /** Dev-only: surfaced by the mock backend so the UI can link to the reset page. */
  devToken?: string;
  devResetUrl?: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/** `name` is the first name, as in the backend; the email is the sign-in and cannot change. */
export interface UpdateProfileRequest {
  name: string;
  lastName: string;
}

export interface MessageResponse {
  message: string;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: ({ remember: _remember, ...body }) => ({ url: 'auth/login', method: 'POST', body }),
      transformResponse: (raw: BackendAuthResponse) => normalizeAuthResponse(raw),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          tokenStorage.setTokens(data.tokens, { remember: arg.remember ?? true });
          dispatch(authSucceeded(data.user));
          try {
            const profile = await dispatch(
              authApi.endpoints.profile.initiate(undefined, { forceRefetch: true }),
            ).unwrap();
            dispatch(authSucceeded(profile));
          } catch {
            /* keep login user if profile is unavailable */
          }
        } catch {
          dispatch(authFailed(null));
        }
      },
    }),

    profile: build.query<User, void>({
      query: () => ({ url: 'users/profile' }),
      providesTags: providesTagsOnSuccessConst(['Me']),
      transformResponse: (raw: UserProfileDTO) => profileToUser(raw),
    }),

    logout: build.mutation<void, void>({
      queryFn(_arg, _api, _extra, _baseQuery) {
        tokenStorage.clear();
        return { data: undefined };
      },
      onQueryStarted(_arg, { dispatch }) {
        dispatch(loggedOut());
      },
    }),

    forgotPassword: build.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (body) => ({ url: 'auth/forgot-password', method: 'POST', body }),
    }),

    resetPassword: build.mutation<MessageResponse, ResetPasswordRequest>({
      query: (body) => ({ url: 'auth/reset-password', method: 'POST', body }),
    }),

    updateProfile: build.mutation<{ modified: number }, UpdateProfileRequest>({
      query: (body) => ({ url: 'users/profile', method: 'PUT', body }),
      invalidatesTags: invalidateTagsOnSuccessConst(['Me']),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return; // the form shows the error
        }
        // A fresh token carries the new name: the account menu reads `auth.user`, which only the
        // refresh writes, and offers take their contact person from the token's claims.
        void dispatch(authApi.endpoints.refreshSession.initiate());
      },
    }),

    changePassword: build.mutation<MessageResponse, ChangePasswordRequest>({
      query: ({ currentPassword, newPassword, confirmPassword }) => ({
        url: 'users/profile/password',
        method: 'PUT',
        body: {
          oldPassword: currentPassword,
          newPassword,
          confirmPassword,
        },
      }),
    }),

    refreshSession: build.mutation<RefreshResponse, void>({
      query: () => {
        const refreshToken = tokenStorage.getRefreshToken() ?? '';
        return { url: 'auth/refresh-token', method: 'POST', body: { refreshToken } };
      },
      transformResponse: (raw: BackendAuthResponse) => normalizeAuthResponse(raw),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const remember = tokenStorage.isPersistent() ?? true;
          tokenStorage.setTokens(data.tokens, { remember });
          dispatch(authSucceeded(data.user));
          try {
            const profile = await dispatch(
              authApi.endpoints.profile.initiate(undefined, { forceRefetch: true }),
            ).unwrap();
            dispatch(authSucceeded(profile));
          } catch {
            /* keep refresh user if profile is unavailable */
          }
        } catch {
          /* baseQueryWithReauth already handles the hard failure */
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useProfileQuery,
  useLazyProfileQuery,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useUpdateProfileMutation,
  useRefreshSessionMutation,
} = authApi;
